import { MAX_PREVIEW_SIDE } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelPdf/constants";
import {
  LEGEND_MIN_SCALE,
  LEGEND_PAGE_MARGIN,
  computeLegendDimensions,
} from "../LegendBox";
import { LEGEND_ROW_HEIGHT, LEGEND_WIDTH } from "../LegendBox/constants";
import { computeLegendScale } from "../drawLegendOntoCanvas/computeLegendScale";
import { buildMoncoBrandBlock } from "../drawMoncoBrandLink";
import { rasterizeSvgOntoCanvas } from "../rasterizeSvgOntoCanvas";
import { renderRoomSectionsSvgString } from "../renderRoomSectionsSvgString";
import { truncateLegendItems } from "../truncateLegendItems";
import type { PlacedRoomHeader, PlacedRoomLegend } from "../types";
import type { AppendRoomSectionsPages } from "./types";

// Title and note above the boxes, measured in legend rows so they scale with the boxes.
const HEADER_ROWS = 3;
// A text page reads best with several narrow columns, unlike the single overlay legend.
const MIN_COLUMNS = 3;
const PAGE_BACKGROUND = "#FFFFFF";

export const appendRoomSectionsPages: AppendRoomSectionsPages = async ({
  doc,
  roomSections,
  pdfPageWidth,
  pdfPageHeight,
}) => {
  const { title, note, legends } = roomSections;
  if (legends.length === 0) return;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Failed to get 2D context for room sections canvas");
  }
  const renderScale = Math.min(
    MAX_PREVIEW_SIDE / pdfPageWidth,
    MAX_PREVIEW_SIDE / pdfPageHeight,
  );
  canvas.width = Math.round(pdfPageWidth * renderScale);
  canvas.height = Math.round(pdfPageHeight * renderScale);

  const brandBlock = await buildMoncoBrandBlock({
    doc,
    pdfPageWidth,
    pdfPageHeight,
  });

  const maxItemCount = Math.max(
    ...legends.map((legend) => legend.items.length),
  );
  const legendScale = computeLegendScale({
    pdfPageWidth,
    pdfPageHeight,
    itemCount: maxItemCount + HEADER_ROWS,
  });
  const columnsScale =
    pdfPageWidth /
    ((MIN_COLUMNS + 1) * LEGEND_PAGE_MARGIN + MIN_COLUMNS * LEGEND_WIDTH);
  const scale = Math.min(legendScale, Math.max(LEGEND_MIN_SCALE, columnsScale));

  const margin = LEGEND_PAGE_MARGIN * scale;
  const columnWidth = LEGEND_WIDTH * scale;
  // Math.min/max return their argument unchanged, so this equality is exact.
  const columnCount =
    scale === columnsScale
      ? MIN_COLUMNS
      : Math.max(
          1,
          Math.floor((pdfPageWidth - margin) / (columnWidth + margin)),
        );
  const header: PlacedRoomHeader = {
    title,
    note,
    x: margin,
    y: brandBlock.bottom + margin,
  };
  const contentTop = header.y + LEGEND_ROW_HEIGHT * HEADER_ROWS * scale;
  const contentBottom = pdfPageHeight - margin;

  // First fit: a box goes into the first column with room, then a new column, then a new page.
  const pages: PlacedRoomLegend[][] = [];
  let columnBottoms: number[] = [];
  const startPage = () => {
    pages.push([]);
    columnBottoms = [];
  };
  startPage();

  for (const legend of legends) {
    const items = truncateLegendItems({ items: legend.items, context });
    const { height } = computeLegendDimensions({
      itemCount: items.length,
      scale,
    });
    let columnIndex = columnBottoms.findIndex(
      (bottom) => bottom + height <= contentBottom,
    );
    if (columnIndex === -1) {
      if (columnBottoms.length === columnCount) startPage();
      columnBottoms.push(contentTop);
      columnIndex = columnBottoms.length - 1;
    }
    pages[pages.length - 1].push({
      legend: { items, summary: legend.summary },
      x: margin + columnIndex * (columnWidth + margin),
      y: columnBottoms[columnIndex],
    });
    columnBottoms[columnIndex] += height + margin;
  }

  const orientation = pdfPageWidth >= pdfPageHeight ? "landscape" : "portrait";
  for (const placedLegends of pages) {
    context.fillStyle = PAGE_BACKGROUND;
    context.fillRect(0, 0, canvas.width, canvas.height);

    const svgString = renderRoomSectionsSvgString({
      pdfPageWidth,
      pdfPageHeight,
      pixelWidth: canvas.width,
      pixelHeight: canvas.height,
      scale,
      header,
      placedLegends,
    });
    await rasterizeSvgOntoCanvas({
      svgString,
      context,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
    });

    doc.addPage([pdfPageWidth, pdfPageHeight], orientation);
    // TODO: toBlob instead of toDataURL, as on the plan page, so the spinner keeps running between pages.
    doc.addImage(
      canvas.toDataURL("image/jpeg", 1.0),
      "JPEG",
      0,
      0,
      pdfPageWidth,
      pdfPageHeight,
    );
    brandBlock.draw();
  }
};
